import { Cart } from "@commercetools/platform-sdk"
import { PrimaryButton, StandardXAxisSpace } from "@sixty-six-north/ui-system"
import React, { useEffect, useRef, useState, MutableRefObject } from "react"
import "@adyen/adyen-web/styles/adyen.css"
import "./adyen-override.css"
import { useTranslation } from "react-i18next"
import { Box } from "theme-ui"
import {
  useAdyenValidation,
  useSchemePaymentStrategy
} from "../../adyen/AdyenPaymentStrategies"
import { useSelectedAdyenPaymentType } from "../../adyen/AdyenState"
import { useCartService } from "../../cart/CartServiceHook"
import { LinkProps } from "../../components/Link"
import { usePaymentLink, useProcessingLink } from "../../components/Links"
import { useCheckoutConfig } from "../../layout/CheckoutConfigHooks"
import { NetGiroFrame } from "../../netgiro/NetGiroFrame"
import { useRouter } from "../../routing/useRouter"
import { useCheckoutState } from "../CheckoutState"
import {
  PreviousButtonContainer,
  NextButtonContainer,
  CheckoutButtonsContainer
} from "../components/Buttons"
import {
  usePaymentErrorHandling,
  useCheckoutReviewValidation
} from "../review/ReviewValidation"
import { useNetgiroValidation } from "../review/validators/NetgiroPaymentValidation"
import { success } from "../validation/FormValidator"
import {
  ValidationResult,
  rejectValidationErrors,
  captureSentryErrorsAndThrow
} from "../validation/ValidationError"
import {
  usePaymentProviderState,
  PaymentProvider
} from "./PaymentProviderState"
import { AdyenPaymentMethodType } from "./validation/AdyenPaymentValidation"

export interface AdyenSession {
  id: string
  sessionData: string
}

export const AdyenPaymentPlaceholder: React.FC<{
  type: AdyenPaymentMethodType
  createCheckout: (ref: React.MutableRefObject<HTMLDivElement | null>) => void
}> = ({ type, createCheckout }) => {
  const paymentContainer = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let ignore = false

    if (!type || !paymentContainer.current) {
      // initiateCheckout is not finished yet.
      return
    }

    // The 'ignore' flag is used to avoid double re-rendering caused by React 18 StrictMode
    // More about it here: https://beta.reactjs.org/learn/synchronizing-with-effects#fetching-data
    if (!ignore && createCheckout) {
      createCheckout(paymentContainer)
    }

    return () => {
      ignore = true
    }
  }, [createCheckout, type])

  return (
    <div className={`${type}-container`}>
      <div ref={paymentContainer} className="payment"></div>
    </div>
  )
}

export const AdyenBox: React.FC<{
  paymentContainer: MutableRefObject<HTMLDivElement | null>
  selectedPaymentType: AdyenPaymentMethodType
  disableCheckout: boolean
}> = ({ paymentContainer, selectedPaymentType, disableCheckout }) => {
  const checkoutState = useCheckoutState()

  const is3DSChallenge = !checkoutState.state.showReviewPageOverview

  return (
    <Box
      sx={{
        display: "flex",
        flexGrow: 1,
        flexDirection:
          is3DSChallenge || selectedPaymentType === "paypal" ? "column" : "row",
        justifyContent: is3DSChallenge ? "center" : "flex-end",
        button: {
          cursor: disableCheckout ? "not-allowed" : "pointer"
        },
        width: "100%"
      }}
    >
      <div
        className={`${selectedPaymentType}-container`}
        sx={{ width: "100%" }}
      >
        <div
          ref={paymentContainer}
          className="payment"
          sx={{
            height: is3DSChallenge ? "630px" : "100%",
            width: "100%"
          }}
        ></div>
      </div>
    </Box>
  )
}

const AdyenCardPaymentButton: React.FC<{
  formValidation: () => Promise<LinkProps | undefined>
  disableCheckout: boolean
}> = ({ formValidation, disableCheckout }) => {
  const paymentContainer = useRef<HTMLDivElement | null>(null)
  const checkoutState = useCheckoutState()

  const is3DSChallenge = !checkoutState.state.showReviewPageOverview

  const { onPaymentError } = usePaymentErrorHandling()

  const adyenSchemePayment = useSchemePaymentStrategy(
    paymentContainer,
    formValidation
  )

  return (
    <React.Fragment>
      {is3DSChallenge ? null : (
        <BasicPaymentButton
          disableCheckout={disableCheckout}
          onClick={() =>
            adyenSchemePayment
              .pay()
              .then(() => {
                checkoutState.hideOverview()
              })
              .catch(onPaymentError)
              .then(success)
          }
        />
      )}
      <AdyenBox
        selectedPaymentType={"scheme"}
        paymentContainer={paymentContainer}
        disableCheckout={disableCheckout}
      />
    </React.Fragment>
  )
}

const AdyenManagedPaymentButton: React.FC<{
  selectedPaymentType: AdyenPaymentMethodType
  formValidation: () => Promise<LinkProps | undefined>
  disableCheckout: boolean
}> = ({ selectedPaymentType, formValidation, disableCheckout }) => {
  const paymentContainer = useRef<HTMLDivElement | null>(null)

  const { createCheckout } = useAdyenValidation(
    paymentContainer,
    formValidation
  )

  useEffect(() => {
    let ignore = false

    if (!paymentContainer.current) {
      // initiateCheckout is not finished yet.
      return
    }

    // The 'ignore' flag is used to avoid double re-rendering caused by React 18 StrictMode
    // More about it here: https://beta.reactjs.org/learn/synchronizing-with-effects#fetching-data
    if (!ignore && createCheckout) {
      createCheckout()
    }

    return () => {
      ignore = true
    }
  }, [])

  return (
    <AdyenBox
      paymentContainer={paymentContainer}
      disableCheckout={disableCheckout}
      selectedPaymentType={selectedPaymentType}
    />
  )
}

const BasicPaymentButton: React.FC<{
  disableCheckout: boolean
  onClick: () => ValidationResult
}> = ({ disableCheckout, onClick }) => {
  const { t } = useTranslation("checkout")
  type PayButtonType = "pay" | "loading" | "none"

  const [payButton, setPayButton] = useState<PayButtonType>("pay")

  return (
    <>
      {payButton === "loading" ? (
        <PrimaryButton
          type="submit"
          fill={true}
          disabled
          loading={payButton === "loading"}
        >
          {t("sendingPayment", { defaultValue: "Sending payment" })}
        </PrimaryButton>
      ) : payButton === "pay" ? (
        <Box
          sx={{
            button: {
              cursor: disableCheckout ? "not-allowed" : "pointer"
            }
          }}
        >
          <PrimaryButton
            type="submit"
            disabled={disableCheckout}
            fill={true}
            onClick={() => {
              setPayButton("loading")
              onClick().then(() => "Basic button clicked")
            }}
            id="payNowButton"
          >
            {t("payNow", { defaultValue: "Pay now" })}
          </PrimaryButton>
        </Box>
      ) : (
        <React.Fragment />
      )}
    </>
  )
}

const NetgiroPaymentButton: React.FC<{
  cartId: string
  formValidation: () => Promise<LinkProps | undefined>
  disableCheckout: boolean
}> = ({ cartId, formValidation, disableCheckout }) => {
  const netgiroValidation = useNetgiroValidation(cartId)
  const router = useRouter()
  const { onPaymentError } = usePaymentErrorHandling()

  return (
    <React.Fragment>
      <BasicPaymentButton
        disableCheckout={disableCheckout}
        onClick={() => {
          return formValidation()
            .then(route =>
              netgiroValidation.save().then(() => {
                if (route) router.push(route.href, route.as)
              })
            )
            .catch(onPaymentError)
            .then(success)
        }}
      />
      <NetGiroFrame
        formRef={netgiroValidation.formRef}
        applicationIdRef={netgiroValidation.applicationIdRef}
        signatureRef={netgiroValidation.signatureRef}
        totalAmountRef={netgiroValidation.totalAmountRef}
        referenceNumberRef={netgiroValidation.referenceNumberRef}
        paymentSuccessfulURLRef={netgiroValidation.paymentSuccessfulURLRef}
      />
    </React.Fragment>
  )
}

const usePaymentDetermination = () => {
  const paymentProviders = usePaymentProviderState()

  const selectedPaymentType = useSelectedAdyenPaymentType()

  const isAdyenCardPayment = selectedPaymentType === "scheme"
  const isOtherPaymentType = selectedPaymentType === undefined
  const isAdyenManagedPayment = !isAdyenCardPayment && !isOtherPaymentType
  const isNetgiroPayment =
    paymentProviders.indexOf(PaymentProvider.NetGiro) > -1

  return {
    isAdyenCardPayment,
    isOtherPaymentType,
    isAdyenManagedPayment,
    isNetgiroPayment
  }
}

export const PaymentButtons: React.FC<{ cartId: string }> = ({ cartId }) => {
  const { t } = useTranslation("checkout")
  const router = useRouter()

  const { disabled: disableCheckout } = useCheckoutConfig()

  const paymentLink = usePaymentLink()

  const { onPaymentError } = usePaymentErrorHandling()
  const selectedPaymentType = useSelectedAdyenPaymentType()

  const checkoutState = useCheckoutState()

  const {
    isAdyenCardPayment,
    isAdyenManagedPayment,
    isNetgiroPayment,
    isOtherPaymentType
  } = usePaymentDetermination()

  const is3DSChallenge = !checkoutState.state.showReviewPageOverview

  // const cart = useCartState()
  const processingUrl = useProcessingLink()

  const cartService = useCartService()
  const validation = useCheckoutReviewValidation()

  const formValidation = () => {
    return cartService
      .fullCart(cartId)
      .then(c => c!)
      .then(validation.validate)
      .then(rejectValidationErrors<Cart>("Form validation failed"))
      .then(validation.save)
      .then(rejectValidationErrors("Saving forms failed"))
      .then(() => processingUrl)
      .catch(
        captureSentryErrorsAndThrow("Error during checkout review", cartId)
      )
  }

  const paymentButton = isAdyenCardPayment ? (
    <AdyenCardPaymentButton
      formValidation={formValidation}
      disableCheckout={disableCheckout}
    />
  ) : isAdyenManagedPayment ? (
    <AdyenManagedPaymentButton
      selectedPaymentType={selectedPaymentType as AdyenPaymentMethodType}
      formValidation={formValidation}
      disableCheckout={disableCheckout}
    />
  ) : isNetgiroPayment ? (
    <NetgiroPaymentButton
      cartId={cartId || ""}
      formValidation={formValidation}
      disableCheckout={disableCheckout}
    />
  ) : isOtherPaymentType ? (
    <BasicPaymentButton
      disableCheckout={disableCheckout}
      onClick={() => {
        return formValidation()
          .then(route =>
            route ? router.push(route.href, route.as) : undefined
          )
          .catch(onPaymentError)
          .then(success)
      }}
    />
  ) : null

  return (
    <Box
      sx={{
        px: [24, null, 48, 48, 48, StandardXAxisSpace],
        maxWidth: ["100%", null, null, "80%"],
        mx: "auto",
        my: 40
      }}
    >
      <CheckoutButtonsContainer>
        {!is3DSChallenge && (
          <PreviousButtonContainer link={paymentLink}>
            {t("returnToPayment", { defaultValue: "Return to payment" })}
          </PreviousButtonContainer>
        )}
        <NextButtonContainer>{paymentButton}</NextButtonContainer>
      </CheckoutButtonsContainer>
    </Box>
  )
}
